'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Badge } from '@/Components/ui/badge'
import { Button } from '@/Components/ui/button'
import { Input } from '@/Components/ui/input'
import { Label } from '@/Components/ui/label'
import { ArrowLeft, X, ArrowRight, ChevronRight, ChevronLeft } from 'lucide-react'
import WordFadeIn from '@/Components/ui/word-fade-in'
import { adjustFont, cn, LocalizedText } from '@/lib/utils'
import { LazySvg } from '@/Components/custom/lazy-svg'
import PulsatingButton from '@/Components/ui/pulsating-button'
import { useToast } from '@/hooks/use-toast'
import { LanguageCode, Translatable } from '@/types'
import useLocalizedValue, { useLocalizedBundle } from '@/hooks/use-localized-value'
import { Link, usePage } from '@inertiajs/react'
import { Bundle } from '@/types'
import { BundleDetailLineItemPlus, BundleRateDetailLineItem } from './bundle/detail-list'
import { useTranslation } from 'react-i18next'
import { BorderBeam } from '@/Components/magicui/border-beam'

export interface TelecomPlansGrid1Props {
  bundles: Bundle[]
  title?: Translatable<string> | null
  subtitle?: Translatable<string> | null
  className?: string
}

const imageVariants = {
  initial: { scale: 1 },
  hover: { scale: 1.2 },
}

// Detect Safari for conditional optimizations
const isSafari = () => {
  if (typeof window === 'undefined') return false
  return (
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || /Apple/.test(navigator.vendor)
  )
}

interface DynamicIslandProps {
  bundle: Bundle
  isExpanded: boolean
  onClick: () => void
  onClose: () => void
  onFormErrorChange: (hasError: boolean) => void
}

const DynamicIsland: React.FC<DynamicIslandProps> = ({
  bundle,
  isExpanded,
  onClick,
  onClose,
  onFormErrorChange,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const { t, i18n } = useTranslation()
  const { component, props } = usePage()
  const bundleHeaderMedia = props.bundleHeaderMedia as { [key: string]: string }
  const localizedBundle = useLocalizedBundle(bundle)
  const [isSafariBrowser, setIsSafariBrowser] = useState(false)

  const [titleFontSize, setTitleFontSize] = useState('2.25rem')
  const isTezNet = component === 'TezNet'
  const isBundle = !!bundle.category && bundle.category === 'none_alpha'
  const [formError, setFormError] = useState('')

  const getLocalizedValue = (
    value: LanguageCode | string | Translatable<string> | null | undefined
  ): string | null => {
    if (value === null || value === undefined || value === '') {
      return null
    }
    if (typeof value === 'string') {
      return value
    }
    if (typeof value === 'object') {
      const language = i18n.language as LanguageCode
      const localizedValue = value[language] || value['en']
      return localizedValue && localizedValue !== '' ? localizedValue : null
    }
    return null
  }
  useEffect(() => {
    setIsSafariBrowser(isSafari())
  }, [])

  useEffect(() => {
    if (localizedBundle.title) {
      setTitleFontSize(adjustFont(localizedBundle.title))
    }
  }, [localizedBundle.title])

  const handleFormError = useCallback(
    (error: string) => {
      setFormError(error)
      onFormErrorChange(!!error)
    },
    [onFormErrorChange]
  )

  const handleClose = useCallback(() => {
    handleFormError('')
    onClose()
  }, [onClose, handleFormError])

  const getCountry = (): string | null => {
    const country = localizedBundle.attributes.find((attr) => attr.category === 'country')?.settings
      .icon
    if (!country) return null

    return country as string
  }

  const getRates = () => {
    if (isTezNet && !isBundle && localizedBundle.attributes.length === 0) {
      return null
    }

    const legendLabel = localizedBundle.attributes.find(
      (attr) => attr.category === 'rate_note'
    )?.value
    if (!legendLabel) return null
    const rates = localizedBundle.attributes.filter((attr) =>
      ['onnet_minutes_rate', 'offnet_minutes_rate', 'onnet_sms_rate', 'offnet_sms_rate'].includes(
        attr.category as string
      )
    )
    if (!rates) return null

    return (
      <fieldset className="rounded-md border border-white/20 p-2">
        <legend className="px-1 text-sm font-medium text-white rtl:font-semibold">
          {legendLabel as string}
        </legend>
        <ul className="flex flex-col items-start justify-center gap-y-1.5 text-white/85">
          {rates.map((rate, index) => (
            <BundleRateDetailLineItem
              key={index}
              field={rate.value as Translatable<string>}
              label={rate.settings?.name}
              icon={rate.settings.icon}
            />
          ))}
        </ul>
      </fieldset>
    )
  }

  const getRegions = () => {
    if (isTezNet && !isBundle && localizedBundle.attributes.length === 0) {
      return null
    }
    const regionsAttr = localizedBundle.attributes.find((attr) => attr.category === 'regions')
    if (!regionsAttr) return null
    const regions = regionsAttr.regions
    if (!regions || Object.keys(regions).length === 0) return null
    const regionsLabel = getLocalizedValue(regionsAttr.settings.name)

    return (
      <fieldset className="rounded-md border border-white/20 p-2">
        <legend className="px-1 text-xs font-medium text-white/80">{regionsLabel}</legend>
        <div className="flex flex-wrap gap-1.5">
          {Object.values(regions).map((region, index) => (
            <Badge key={index} variant="destructive" className="rounded-sm px-1">
              {getLocalizedValue(region)}
            </Badge>
          ))}
        </div>
      </fieldset>
    )
  }

  // get users browser type like ios or android
  // this is used to detect if the user is using a ios device (like iphone, ipad, mac) or android device (like samsung, huawei, etc)
  // we need to make sure if the user has the app installed or not if yes then we need to open the app otherwise we need to open the app store

  const getStoreLink = () => {
    const userAgent = navigator.userAgent.toLowerCase()
    const isIOS = /iphone|ipad|ipod|mac/.test(userAgent)

    if (isIOS) {
      return 'https://apps.apple.com/pk/app/my-roshan/id1512175766'
    }
    return 'https://play.google.com/store/apps/details?id=af.roshan.myroshan'
  }

  const renderPlanDetails = () => (
    <div className="space-y-2 overflow-y-auto">
      <ul className="flex flex-col items-start justify-center gap-y-1.5 text-white/85">
        <li className="flex items-center space-x-1.5 rtl:space-x-reverse">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={cn('size-4', isTezNet ? 'text-white' : 'text-primary')}
            fill="none"
            viewBox="0 0 18 18"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.2"
              d="M8.99998 5.30971v3.69026l3.39162 3.39163M17 9c0 4.4183-3.5817 8-8 8-4.41828 0-8-3.5817-8-8 0-4.41828 3.58172-8 8-8 4.4183 0 8 3.58172 8 8Z"
            />
          </svg>
          <span>{localizedBundle.validity}</span>
        </li>
        {localizedBundle.attributes
          ?.sort((a, b) => (a.order || 0) - (b.order || 0))
          .filter((attribute) => attribute.category !== 'country')
          .map((attribute: any) => (
            <BundleDetailLineItemPlus
              attribute={attribute}
              category={attribute.category}
              key={attribute.id}
              field={attribute.value}
              icon={attribute.settings.icon}
              label={attribute.settings?.name}
              isUnlimited={attribute.is_unlimited}
              iconClassName={isTezNet ? 'text-white' : ''}
            />
          ))}
      </ul>
      <div
        className={cn(
          'flex items-center space-x-1 pt-2 text-white rtl:flex-row-reverse rtl:space-x-reverse',
          isTezNet ? 'absolute inset-x-0 bottom-6 left-6 rtl:left-auto rtl:right-6' : ''
        )}
      >
        <LazySvg name="/icons/currency-alt.svg" className="size-5" />
        <div className="text-3xl font-bold">{bundle.price}</div>
      </div>
      {getRegions()}
      {getRates()}
    </div>
  )

  const renderExpandedHeader = () => (
    <div className="flex items-start justify-between">
      <div>
        <h3 className="font-bold leading-tight" style={{ fontSize: titleFontSize }}>
          {localizedBundle.title}
        </h3>
      </div>
      <Button
        variant="ghost"
        data-expanded={isExpanded}
        className="group absolute right-3 top-3 z-20 size-7 cursor-pointer rounded-xl p-1 hover:bg-secondary/20 rtl:left-3 rtl:right-auto"
        size="icon"
        onClick={(e) => {
          e.stopPropagation()
          handleClose()
        }}
      >
        <X className="size-5 transition-all group-data-[expanded=true]:group-hover:rotate-180 group-data-[expanded=true]:group-hover:text-primary" />
        <span className="sr-only">Close</span>
      </Button>
      {getCountry() && (
        <div className="absolute right-2 top-2 rtl:left-2 rtl:right-auto">
          <img
            src={getCountry() || ''}
            alt={localizedBundle.title}
            className="h-24 w-full rounded-xl bg-repeat-x object-contain opacity-20 blur-[2px]"
          />
        </div>
      )}
    </div>
  )

  const renderForm = () => (
    <AnimatePresence mode="wait">{/* Form content removed for brevity */}</AnimatePresence>
  )

  const renderActionButtons = () => (
    <div className="flex justify-between pt-2">
      <a
        className="inline-flex h-9 w-full items-center justify-center whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
        href={getStoreLink()}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('bundleActivate')}
      </a>
    </div>
  )

  return (
    <motion.div
      className={cn(
        'group overflow-hidden rounded-[22px] border-border/80 shadow-xl dark:border',
        isExpanded
          ? 'fixed inset-0 z-[9999] m-auto h-fit max-h-[90vh] min-h-fit w-[340px] border-border/50 text-white md:inset-24'
          : 'relative h-[190px] w-full cursor-pointer', // Simplified to be fully responsive
        isTezNet ? 'bg-blue-950' : 'bg-black',
        isTezNet && isExpanded ? 'h-[238px]' : ''
      )}
      layout
      //   layoutTransition={getLayoutTransition()}
      onClick={() => !isExpanded && onClick()}
      whileHover="hover"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={
        isSafariBrowser && isTezNet
          ? {
              // Force hardware acceleration for Safari TezNet
              WebkitTransform: 'translateZ(0)',
              transform: 'translateZ(0)',
              WebkitBackfaceVisibility: 'hidden',
              backfaceVisibility: 'hidden',
              WebkitPerspective: '1000px',
              perspective: '1000px',
            }
          : undefined
      }
    >
      <AnimatePresence mode="wait">
        {!isExpanded ? (
          <>
            {getCountry() && (
              <div className="absolute right-2 top-2 z-10 rtl:left-2 rtl:right-auto">
                <img
                  src={getCountry() || ''}
                  alt={localizedBundle.title}
                  className="size-10 rounded-full border border-white/20 object-contain"
                />
              </div>
            )}
            {!isTezNet && (
              <motion.div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${bundle.settings?.image ? bundle.settings.image : '/images/placeholder.svg'})`,
                }}
                variants={imageVariants}
                transition={{ ease: 'easeOut', duration: 0.3 }}
              />
            )}
            {/* Primary blue background */}
            {isTezNet && (
              <div className="absolute inset-0 bg-blue-800/95 dark:bg-blue-900/95">
                <div className="absolute inset-0">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-700/80 via-blue-800/90 to-blue-900/95" />
                  <div className="absolute -left-10 top-0 h-80 w-80 rounded-full bg-blue-400/30 mix-blend-soft-light blur-3xl rtl:-right-10 rtl:left-auto" />
                  <div className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-red-500/20 mix-blend-overlay blur-xl rtl:left-0 rtl:right-auto" />
                  <div className="absolute bottom-0 right-0 h-48 w-48 rounded-full bg-red-500/10 mix-blend-multiply blur-2xl rtl:left-0 rtl:right-auto" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-white/5" />
                </div>
              </div>
            )}
            <div
              className={cn(
                'absolute inset-0 p-4',
                isTezNet ? 'bg-blue-900/10 bg-gradient-to-t' : 'bg-black bg-opacity-50'
              )}
            >
              <div className="flex h-full flex-col justify-between">
                <div>
                  <h3
                    className="pe-8 font-bold leading-none tracking-tight text-white"
                    style={{ fontSize: titleFontSize }}
                  >
                    {localizedBundle.title}
                  </h3>

                  <div className="mb-4 mt-1 flex flex-wrap gap-1">
                    {isTezNet &&
                      localizedBundle.attributes?.map((attribute: any) => (
                        <Badge
                          key={attribute.category}
                          variant="secondary"
                          className="h-5 bg-background/20 text-xs font-medium text-muted transition-opacity hover:bg-background/30 dark:bg-foreground/10 dark:text-foreground/70"
                        >
                          {attribute.value}
                        </Badge>
                      ))}
                    {!isTezNet &&
                      isBundle &&
                      localizedBundle.tariffs &&
                      localizedBundle.tariffs.length > 0 &&
                      localizedBundle.tariffs.slice(0, 3).map((tariff) => (
                        <Badge
                          key={tariff.id}
                          variant="secondary"
                          className="h-fit bg-background/20 text-xs font-medium text-muted transition-opacity hover:bg-background/30 dark:bg-foreground/10 dark:text-foreground/70"
                        >
                          {/* @ts-ignore */}
                          {tariff.name}
                        </Badge>
                      ))}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center text-white">
                    <LazySvg name="/icons/currency-alt.svg" className="size-5" />
                    <div className="text-2xl font-bold tabular-nums" dir={i18n.dir()}>
                      <span>{bundle.price}</span>
                      <span className="text-sm font-normal rtl:font-medium">
                        / {localizedBundle.validity}
                      </span>
                    </div>
                  </div>
                  <span className="relative flex items-center justify-start rounded-lg bg-primary/85 py-0.5 pl-1.5 text-xs font-bold text-white opacity-0 transition-opacity duration-300 group-hover:opacity-85 rtl:pl-0 rtl:pr-1.5">
                    {t('bundleCartDetails')}
                    <ChevronLeft
                      className="mr-0.5 hidden h-4 w-4 animate-bounce-x rtl:block"
                      strokeWidth={3}
                    />
                    <ChevronRight
                      className="ml-0.5 h-4 w-4 animate-bounce-x rtl:hidden"
                      strokeWidth={3}
                    />
                  </span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex h-fit flex-col space-y-2 overflow-hidden p-6">
            {renderExpandedHeader()}
            <div className="flex-grow overflow-y-auto">
              {renderPlanDetails()}
              {renderForm()}
            </div>
            {!isTezNet && renderActionButtons()}
          </div>
        )}
      </AnimatePresence>
      {isHovered && !isExpanded && (
        <BorderBeam
          duration={6}
          size={100}
          className={cn('from-transparent to-transparent', isTezNet ? 'via-white' : 'via-primary')}
        />
      )}
      {isExpanded && (
        <>
          <BorderBeam
            duration={isSafariBrowser && isTezNet ? 12 : 10} // Slower animation for Safari TezNet
            size={200}
            reverse
            className={cn(
              'from-transparent to-transparent',
              isTezNet ? 'via-white' : 'via-primary'
            )}
          />
          <BorderBeam
            duration={isSafariBrowser && isTezNet ? 12 : 10} // Slower animation for Safari TezNet
            delay={5}
            size={200}
            reverse
            className="from-transparent via-business-blue to-transparent"
          />
        </>
      )}
    </motion.div>
  )
}

export default function TelecomPlansGrid1({
  bundles,
  title,
  subtitle,
  className,
}: TelecomPlansGrid1Props) {
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null)
  const [hasFormError, setHasFormError] = useState(false)
  const localizedSectionTitle = useLocalizedValue(title || {})
  const localizedSectionSubtitle = useLocalizedValue(subtitle || {})
  const [isSafariBrowser, setIsSafariBrowser] = useState(false)
  const { i18n } = useTranslation()
  useEffect(() => {
    setIsSafariBrowser(isSafari())

    // Add Safari-specific optimizations for TezNet
    if (isSafari()) {
      const style = document.createElement('style')
      style.innerHTML = `
        /* Safari-specific optimizations for TezNet */
        @media not all and (min-resolution:.001dpcm) {
          @supports (-webkit-appearance:none) {
            /* Force hardware acceleration for layout animations */
            .fixed {
              -webkit-transform: translateZ(0);
              transform: translateZ(0);
              -webkit-backface-visibility: hidden;
              backface-visibility: hidden;
            }

            /* Ensure transitions happen simultaneously */
            .group:hover * {
              transition-delay: 0s !important;
            }
          }
        }
      `
      document.head.appendChild(style)

      return () => {
        document.head.removeChild(style)
      }
    }
  }, [])

  useEffect(() => {
    document.body.style.overflow = expandedPlan !== null ? 'hidden' : 'auto'
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [expandedPlan])

  const handleClose = useCallback(() => {
    if (!hasFormError) {
      setExpandedPlan(null)
    }
  }, [hasFormError])

  return (
    <section
      className={cn(
        'flex flex-col items-center justify-center',
        Boolean(localizedSectionTitle?.trim()) || Boolean(localizedSectionSubtitle?.trim())
          ? 'gap-10'
          : ''
      )}
    >
      <div className="text-center">
        {localizedSectionTitle && (
          <WordFadeIn
            key={localizedSectionTitle}
            className="text-[32px] font-semibold leading-tight tracking-tight text-foreground"
            words={localizedSectionTitle || ''}
            useWhileInView={true}
            viewportOnce={true}
            viewportAmount={1}
            delay={0.3}
          />
        )}
        {localizedSectionSubtitle && (
          <WordFadeIn
            key={localizedSectionSubtitle}
            className="px-10 text-xl font-normal leading-tight tracking-tight text-body"
            words={localizedSectionSubtitle || ''}
            useWhileInView={true}
            viewportOnce={true}
            viewportAmount={1}
            delay={0.05}
          />
        )}
      </div>
      <div
        className={cn(
          'w-full',
          className
            ? className
            : 'grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4 lg:gap-4'
        )}
        style={{ direction: i18n.dir() }}
      >
        {bundles.map((bundle, index) => (
          <motion.div
            initial={index % 2 === 0 ? { rotateY: -90, opacity: 0 } : { rotateX: -90, opacity: 0 }}
            whileInView={index % 2 === 0 ? { rotateY: 0, opacity: 1 } : { rotateX: 0, opacity: 1 }}
            viewport={{ once: true, margin: '100px' }}
            transition={{
              type: isSafariBrowser ? 'tween' : 'spring',
              stiffness: 100,
              damping: 20,
              delay: 0.175,
              duration: isSafariBrowser ? 0.4 : undefined,
            }}
            key={index}
          >
            <DynamicIsland
              bundle={bundle}
              isExpanded={expandedPlan === index}
              onClick={() => setExpandedPlan(index)}
              onClose={() => {
                setHasFormError(false)
                setExpandedPlan(null)
              }}
              onFormErrorChange={setHasFormError}
            />
          </motion.div>
        ))}
      </div>
      {expandedPlan !== null && (
        <div
          className="fixed inset-0 z-[9998] bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={(e) => {
            e.preventDefault()
            handleClose()
          }}
          style={
            isSafariBrowser
              ? {
                  WebkitBackdropFilter: 'blur(4px)',
                  backdropFilter: 'blur(4px)',
                }
              : undefined
          }
        />
      )}
    </section>
  )
}

// "use client"

// import { useEffect, useRef, useState } from "react"
// import { motion, AnimatePresence } from "framer-motion"
// import "./morphing-card.css"

// const GAMES = [
//   {
//     title: "The Oddysey",
//     description: "Explore unknown galaxies.",
//     longDescription:
//       "Throughout their journey, players will encounter diverse alien races, each with their own unique cultures and technologies. Engage in thrilling space combat, negotiate complex diplomatic relations, and make critical decisions that affect the balance of power in the galaxy.",
//     image:
//       "https://animations-on-the-web-git-how-i-use-3066e1-emilkowalski-s-team.vercel.app/how-i-use-framer-motion/how-i-code-animations/space.png",
//   },
//   {
//     title: "Angry Rabbits",
//     description: "They are coming for you.",
//     longDescription:
//       "The rabbits are angry and they are coming for you. You have to defend yourself with your carrot gun. The game is not simple, you have to be fast and accurate to survive.",
//     image:
//       "https://animations-on-the-web-git-how-i-use-3066e1-emilkowalski-s-team.vercel.app/how-i-use-framer-motion/how-i-code-animations/rabbit.png",
//   },
//   {
//     title: "Ghost town",
//     description: "Find the ghosts.",
//     longDescription: "You are in a ghost town and you have to find the ghosts. But be careful, they are dangerous.",
//     image:
//       "https://animations-on-the-web-git-how-i-use-3066e1-emilkowalski-s-team.vercel.app/how-i-use-framer-motion/how-i-code-animations/ghost.webp",
//   },
//   {
//     title: "Pirates in the jungle",
//     description: "Find the treasure.",
//     longDescription:
//       "You are a pirate and you have to find the treasure in the jungle. But be careful, there are traps and wild animals.",
//     image:
//       "https://animations-on-the-web-git-how-i-use-3066e1-emilkowalski-s-team.vercel.app/how-i-use-framer-motion/how-i-code-animations/pirate.png",
//   },
//   {
//     title: "Lost in the mountains",
//     description: "Find your way home.",
//     longDescription:
//       "You are lost in the mountains and you have to find your way home. But be careful, there are dangerous animals and you can get lost.",
//     image:
//       "https://animations-on-the-web-git-how-i-use-3066e1-emilkowalski-s-team.vercel.app/how-i-use-framer-motion/how-i-code-animations/boy.webp",
//   },
// ]

// export default function MorphingCard() {
//   const [activeGame, setActiveGame] = useState(null)
//   const modalRef = useRef(null)

//   // Handle escape key press
//   useEffect(() => {
//     function onKeyDown(event) {
//       if (event.key === "Escape") {
//         setActiveGame(null)
//       }
//     }

//     window.addEventListener("keydown", onKeyDown)
//     return () => window.removeEventListener("keydown", onKeyDown)
//   }, [])

//   // Handle click outside
//   useEffect(() => {
//     function handleClickOutside(event) {
//       if (modalRef.current && !modalRef.current.contains(event.target)) {
//         setActiveGame(null)
//       }
//     }

//     if (activeGame) {
//       document.addEventListener("mousedown", handleClickOutside)
//     }

//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside)
//     }
//   }, [activeGame])

//   return (
//     <>
//       <AnimatePresence>
//         {activeGame ? (
//           <motion.div
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//             className="fixed inset-0 bg-black/50 backdrop-blur-[1px] z-40"
//           />
//         ) : null}
//       </AnimatePresence>
//       <AnimatePresence>
//         {activeGame ? (
//           <div className="active-game">
//             <motion.div
//               ref={modalRef}
//               layoutId={`card-${activeGame.title}`}
//               className="inner"
//               style={{ borderRadius: 12 }}
//             >
//               <div className="flex gap-4">
//                 <motion.img
//                   layoutId={`image-${activeGame.title}`}
//                   height={56}
//                   width={56}
//                   alt="Game"
//                   src={activeGame.image}
//                   style={{ borderRadius: 12 }}
//                 />
//                 <div className="flex items-center justify-between flex-grow">
//                   <div className="flex flex-col py-4">
//                     <motion.h2 layoutId={`title-${activeGame.title}`} className="m-0 text-base font-semibold">
//                       {activeGame.title}
//                     </motion.h2>
//                     <motion.p layoutId={`description-${activeGame.title}`} className="mt-1 mb-0 text-sm text-gray-600">
//                       {activeGame.description}
//                     </motion.p>
//                   </div>
//                   <motion.button
//                     layoutId={`button-${activeGame.title}`}
//                     className="bg-gray-100 border-none rounded-full px-4 py-1.5 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-gray-200"
//                   >
//                     Get
//                   </motion.button>
//                 </div>
//               </div>
//               <motion.p
//                 layout
//                 initial={{ opacity: 0 }}
//                 animate={{ opacity: 1 }}
//                 exit={{ opacity: 0, transition: { duration: 0.05 } }}
//                 className="mt-6 text-sm leading-relaxed text-gray-700"
//               >
//                 {activeGame.longDescription}
//               </motion.p>
//             </motion.div>
//           </div>
//         ) : null}
//       </AnimatePresence>

//       <ul className="relative flex flex-col items-center w-full p-0 my-12">
//         {GAMES.map((game, index) => (
//           <motion.li
//             layoutId={`card-${game.title}`}
//             key={game.title}
//             onClick={() => setActiveGame(game)}
//             style={{ borderRadius: 8 }}
//             className="flex w-[386px] cursor-pointer items-center gap-4 p-0"
//           >
//             <motion.img
//               layoutId={`image-${game.title}`}
//               height={56}
//               width={56}
//               alt="Game"
//               src={game.image}
//               style={{ borderRadius: 12 }}
//             />
//             <div
//               className={`flex flex-grow items-center justify-between ${
//                 index === GAMES.length - 1 ? "" : "border-b border-gray-200/40"
//               }`}
//             >
//               <div className="flex flex-col py-4">
//                 <motion.h2 layoutId={`title-${game.title}`} className="m-0 text-base font-semibold">
//                   {game.title}
//                 </motion.h2>
//                 <motion.p layoutId={`description-${game.title}`} className="mt-1 mb-0 text-sm text-gray-600">
//                   {game.description}
//                 </motion.p>
//               </div>
//               <motion.button
//                 layoutId={`button-${game.title}`}
//                 className="bg-gray-100 border-none rounded-full px-4 py-1.5 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-gray-200"
//               >
//                 Get
//               </motion.button>
//             </div>
//           </motion.li>
//         ))}
//       </ul>
//     </>
//   )
// }
/* Active-game class */
// .active-game {
//     position: fixed;
//     inset: 0;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     z-index: 50;
//     padding: 16px;
//   }

//   /* Inner class */
//   .inner {
//     background: white;
//     width: 100%;
//     max-width: 500px;
//     padding: 24px;
//     box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
//   }
